import { Component, inject, CUSTOM_ELEMENTS_SCHEMA, ViewEncapsulation, EventEmitter, Output, OnDestroy } from '@angular/core';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { environment } from '../../../environments/environment';
import { filter, map, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, of } from 'rxjs';
import { isDevelopmentEnvironment } from '../../constants';
import { TokenPropertyService } from '../services/token-property.service';
import { SettingsService } from '../services/user/settings.service';
import { QuicksightService } from '../services/user/quicksight.service';
import { ChatEligibilityService } from '../services/chat-eligibility.service';
import { isFeatureEnabledInUserSettings, getUserActiveProducts } from '../../utils';

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
})
export class NavigationComponent implements OnDestroy {
  private router = inject(Router);
  private tokenPropertyService = inject(TokenPropertyService);
  private settingsService = inject(SettingsService);
  private quicksightService = inject(QuicksightService);
  private chatEligibilityService = inject(ChatEligibilityService);
  private destroy$ = new Subject<void>();
  public isInDevEnvironment = isDevelopmentEnvironment();
  public showAIButton = false; // computed dynamically
  public sso_internal_client_id = environment.sso_internal_client_id;
  public internalCognitoAuthorizeURL = environment.internalCognitoAuthorizeURL;

  @Output() openChat = new EventEmitter<void>();

  constructor() {
    this.updateShowAIButton();
    // Subscribe to token availability changes
    this.tokenPropertyService['tokenAvailable$']?.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Force the QuickSight service to refresh its tokens to clear any cached client code
      this.quicksightService.refreshTokens();
      this.updateShowAIButton();
    });
  }

  private updateShowAIButton(): void {
    // First check chat eligibility - if false, this takes priority and overrides everything
    const isChatEligible = this.chatEligibilityService.isChatEligible();
    
    if (!isChatEligible) {
      this.showAIButton = false;
      this.logInfo('Chat eligibility check failed - hiding AI button');
      return;
    }
    
    // If chat is eligible, then check the user's settings
    // This allows user preferences to control visibility for eligible users
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'AI', 'Chat', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ).subscribe(isFeatureEnabled => {
      this.showAIButton = isFeatureEnabled;
      this.logInfo('Chat eligibility passed, user settings check:', isFeatureEnabled);
    });
    
    // Log debug information for development
    const debugInfo = this.chatEligibilityService.getEligibilityDebugInfo();
    this.logInfo('Navigation chat visibility debug:', debugInfo);
  }

  // Using TokenPropertyService to check if user is admin - using toSignal to convert Observable to Signal
  public isAdminUser = toSignal(
    this.tokenPropertyService.propertyHasValue('custom:adminFlag', true).pipe(
      takeUntil(this.destroy$)
    ),
    { initialValue: false }
  );
  
  // Check if user has access to Leaves functionality (HR or Supervisor persona)
  public hasLeavesAccess = toSignal(
    this.tokenPropertyService.hasLeavesPersona().pipe(
      takeUntil(this.destroy$)
    ),
    { initialValue: false }
  );

  // Check if user has internal access (DigitalExperience AD group)
  public isInternalUser = toSignal(
    this.tokenPropertyService.hasInternalAccess().pipe(
          takeUntil(this.destroy$)
        ),
    { initialValue: false }
  );

  // Check if AI Chat feature is enabled (with precedence: chat eligibility FALSE > user settings)
  // This is kept for backwards compatibility but the actual logic is in updateShowAIButton()
  public isAIChatEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'AI', 'Chat', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: false }
  );

  // Check if Reports & Dashboards should be visible based on user settings
  public isReportsAndDashboardsEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'Content', 'Reports', userActiveProducts) || 
               isFeatureEnabledInUserSettings(settings, 'Content', 'Dashboards', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: true }
  );

  // Check if Subscriptions feature is enabled based on user settings
  public isSubscriptionsEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'General_Features', 'Subscriptions', userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: true }
  );

  // Check if Help section should be visible based on user settings - true if either FAQ or Glossary is enabled
  public isHelpEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'Help', undefined, userActiveProducts);
      }),
      takeUntil(this.destroy$)
    ),
    { initialValue: true }
  );

  isAdmin = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
      map(() => this.checkUrl('admin'))
    ),
    { initialValue: this.checkUrl('admin') }
  );

  private checkUrl(uri: string): boolean {
    const url = this.router.url;
    // Check if the URL contains a specific string
    return url.includes(uri);
  }
  onButtonClicked(event: any): void {
    // Handle the button click event here
    // this.router.navigate(['/chat']);
    this.openChat.emit();
  }

    /**
   * Log helper methods that only log in non-production environments
   */
  private logInfo(message: string, ...data: any[]): void {
    if (!environment.production) {
      console.log(`[Navigation component] ${message}`, ...data);
    }
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

